import React, { useState, useEffect } from 'react';
import Icon from '../components/Icon';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../components/NotificationsPanel';
import { apiFetch } from '../config/api';
import { useLocation } from 'react-router-dom';
import Chat from '../components/Chat';
import useUnreadMessages from '../hooks/useUnreadMessages';
import B2BPanel from '../components/B2BPanel';
import NotificationsPanel from '../components/NotificationsPanel';
import TrainingsPanel from '../components/TrainingsPanel';
import { toast } from '../components/ToastProvider';
import '../styles/Dashboard.css';
import '../styles/AdminDashboard.css';

const FranchiseeDashboard = () => {
  const { user } = useAuth();
  const { unread: notifUnread } = useNotifications(user?.id);
  const { unreadCount: chatUnread } = useUnreadMessages(user);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');

  const [appForm, setAppForm] = useState({
    salonName: '', city: '', address: '', area: '', description: '', category: 'both',
  });
  const [applications, setApplications] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (location.state?.openTab) {
      setActiveTab(location.state.openTab);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail.tab);
    window.addEventListener('hairkus-open-tab', handler);
    return () => window.removeEventListener('hairkus-open-tab', handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    apiFetch('/b2b/franchise')
      .then(data => {
        const myApps = data || [];
        setApplications(myApps);
        const myApproved = myApps.filter(a => a.status === 'approved');
        setStats({
          salons: myApproved.length,
          bookings: 0,
          revenue: 0,
        });
      })
      .catch(() => {});
  }, [user]);

  const [stats, setStats] = useState({ salons: 0, bookings: 0, revenue: 0 });

  const handleAppChange = (e) => setAppForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmitApp = async (e) => {
    e.preventDefault();
    if (!appForm.salonName || !appForm.city || !appForm.address) {
      toast.error('Wypełnij wymagane pola: nazwa, miasto, adres');
      return;
    }
    setSubmitting(true);
    try {
      const newApp = await apiFetch('/b2b/franchise', {
        method: 'POST',
        body: JSON.stringify(appForm),
      });
      setApplications(prev => [...prev, newApp]);
      setAppForm({ salonName: '', city: '', address: '', area: '', description: '', category: 'both' });
      toast.success('Wniosek wysłany! Administrator skontaktuje się wkrótce.');
      setActiveTab('applications');
    } catch (err) {
      toast.error(err.message || 'Błąd podczas wysyłania wniosku');
    } finally {
      setSubmitting(false);
    }
  };

  const approvedSalons = applications.filter(a => a.status === 'approved');
  const pendingApps = applications.filter(a => a.status === 'pending');

  const statusStyle = {
    pending:  { bg: '#fef3c7', color: '#92400e', label: 'Oczekuje' },
    approved: { bg: '#d1fae5', color: '#065f46', label: 'Zaakceptowany' },
    rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Odrzucony' },
  };

  const categoryLabels = { male: 'Męski', female: 'Damski', both: 'Damsko-męski' };
  const categoryIcons  = { male: '♂', female: '♀', both: '♂♀' };

  const tabs = [
    { id: 'overview',      icon: 'stats',        label: 'Przegląd' },
    { id: 'salons',        icon: 'salon',        label: `Moje salony (${approvedSalons.length})` },
    { id: 'applications',  icon: 'application',  label: `Wnioski (${applications.length})` },
    { id: 'new',           icon: 'add',          label: 'Nowy wniosek' },
    { id: 'b2b',           icon: 'package',      label: 'Zamówienia B2B' },
    { id: 'trainings',     icon: 'staff',        label: 'Szkolenia' },
    { id: 'notifications', icon: 'notification', label: 'Powiadomienia' },
    { id: 'messages',      icon: 'chat',         label: 'Kontakt z adminem' },
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        <div className="dashboard-header">
          <h1>Panel Franczyzobiorcy</h1>
          <p>Witaj, {user?.firstName}! <Icon name="franchisee" style={{marginRight:'7px'}} /></p>
        </div>

        {/* Statystyki */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon"><Icon name="salon" style={{marginRight:'7px'}} /></div>
            <div className="stat-content"><h3>{stats.salons}</h3><p>Aktywne salony</p></div>
          </div>
          <div className="stat-card highlight-pending">
            <div className="stat-icon"><Icon name="pending" color="#f59e0b" style={{marginRight:'5px'}} /></div>
            <div className="stat-content"><h3>{pendingApps.length}</h3><p>Oczekujące wnioski</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Icon name="calendar" style={{marginRight:'7px'}} /></div>
            <div className="stat-content"><h3>{stats.bookings}</h3><p>Rezerwacje</p></div>
          </div>
          <div className="stat-card highlight-revenue">
            <div className="stat-icon"><Icon name="money" style={{marginRight:'5px'}} /></div>
            <div className="stat-content"><h3>{stats.revenue} zł</h3><p>Przychód</p></div>
          </div>
        </div>

        {/* Zakładki */}
        <div className="admin-tabs">
          {tabs.map(t => {
            const isMessages = t.id === 'messages';
            const isNotifications = t.id === 'notifications';
            return (
              <button key={t.id}
                className={`admin-tab ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => setActiveTab(t.id)}
                style={(isNotifications || isMessages) ? {position:'relative'} : {}}>
                <Icon name={t.icon} style={{marginRight:'7px'}} />
                {t.id === 'notifications' ? 'Powiadomienia' : t.label}
                {isMessages && chatUnread > 0 && (
                  <span className="tab-unread-badge">{chatUnread > 9 ? '9+' : chatUnread}</span>
                )}
                {isNotifications && activeTab !== 'notifications' && notifUnread > 0 && (
                  <span className="tab-unread-badge">{notifUnread > 9 ? '9+' : notifUnread}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* PRZEGLĄD */}
        {activeTab === 'overview' && (
          <div className="dashboard-content-single">
            <div className="features-grid">
              <div className="feature-card">
                <h3><Icon name="doc" style={{marginRight:'7px'}} /> Status franczyzy</h3>
                {applications.length === 0 ? (
                  <>
                    <p style={{color:'#666', marginBottom:'15px'}}>Nie złożyłeś jeszcze żadnego wniosku o franczyzę.</p>
                    <button className="cta-button" style={{width:'100%',marginTop:'16px',display:'block'}} onClick={() => setActiveTab('new')}>
                      <Icon name="add" style={{marginRight:'7px'}} />Złóż pierwszy wniosek
                    </button>
                  </>
                ) : (
                  <div className="top-list" style={{marginTop:'10px'}}>
                    {applications.slice(0,3).map(a => {
                      const s = statusStyle[a.status] || statusStyle.pending;
                      return (
                        <div key={a.id} className="top-item">
                          <span className="top-name">{a.salon_name}</span>
                          <span style={{padding:'3px 10px', background:s.bg, color:s.color, borderRadius:'12px', fontSize:'0.82em', fontWeight:700}}>
                            {s.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="feature-card feature-card-action" onClick={() => setActiveTab('trainings')} style={{cursor:'pointer'}}>
                <div className="feature-card-icon"><Icon name="staff" color="#1e3c72" size="2x" /></div>
                <h3>Szkolenia pracowników</h3>
                <p>Złóż wniosek o szkolenie dla pracowników z zakresu standardów HAIRKUS.</p>
                <button className="cta-button" style={{marginTop:'12px'}} onClick={e=>{e.stopPropagation();setActiveTab('trainings');}}>
                  <Icon name="add" style={{marginRight:'7px'}} />Zarządzaj szkoleniami
                </button>
              </div>
              <div className="feature-card feature-card-action" onClick={() => setActiveTab('b2b')} style={{cursor:'pointer'}}>
                <div className="feature-card-icon"><Icon name="package" color="#1e3c72" size="2x" /></div>
                <h3>Zamówienia B2B</h3>
                <p>Zamów produkty zawodowe dla swoich salonów po cenach hurtowych.</p>
                <button className="cta-button" style={{marginTop:'12px'}} onClick={e=>{e.stopPropagation();setActiveTab('b2b');}}>
                  <Icon name="add" style={{marginRight:'7px'}} />Złóż zamówienie
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MOJE SALONY */}
        {activeTab === 'salons' && (
          <div className="dashboard-content-single">
            <div className="dashboard-card-full">
              <div className="card-header-with-button">
                <h2><Icon name="salon" style={{marginRight:'7px'}} /> Aktywne salony</h2>
                <button className="cta-button" onClick={() => setActiveTab('new')}>+ Nowy wniosek</button>
              </div>
              {approvedSalons.length > 0 ? (
                <div className="admin-cards-grid">
                  {approvedSalons.map(salon => {
                    const salonBookings = [];
                    const rev = 0;
                    return (
                      <div key={salon.id} className="admin-salon-card">
                        <div className="admin-salon-header">
                          <h3>{salon.salon_name}</h3>
                          <span className="active-badge"><Icon name="confirmed" color="#10b981" style={{marginRight:'5px'}} /> Aktywny</span>
                        </div>
                        <p><Icon name="location" color="#1e3c72" style={{marginRight:'5px'}} /> {salon.city}, {salon.address}</p>
                        {salon.area && <p> {salon.area} m²</p>}
                        {salon.category && (
                          <p style={{fontSize:'0.85em',color:'#6b7280'}}>
                            Kategoria: <strong>{(categoryIcons[salon.category] ? categoryIcons[salon.category] + ' ' : '')}{categoryLabels[salon.category] || salon.category}</strong>
                          </p>
                        )}
                        <div className="salon-stats-mini">
                          <span>Rezerwacji: <strong>{salonBookings.length}</strong></span>
                          <span>Przychód: <strong>{rev} zł</strong></span>
                        </div>
                        <p style={{fontSize:'0.8em',color:'#aaa',marginTop:'8px'}}>
                          Zaakceptowano: {new Date(salon.submitted_at || salon.submittedAt).toLocaleDateString('pl-PL')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><Icon name="salon" style={{marginRight:'7px'}} /></div>
                  <h3>Brak aktywnych salonów</h3>
                  <p>Złóż wniosek o franczyzę i poczekaj na akceptację administratora</p>
                  <button className="cta-button-large" onClick={() => setActiveTab('new')}>Złóż wniosek</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* WNIOSKI */}
        {activeTab === 'applications' && (
          <div className="dashboard-content-single">
            <div className="dashboard-card-full">
              <div className="card-header-with-button">
                <h2><Icon name="doc" style={{marginRight:'7px'}} /> Moje wnioski</h2>
                <button className="cta-button" onClick={() => setActiveTab('new')}>+ Nowy wniosek</button>
              </div>
              {applications.length > 0 ? (
                <div className="table-container">
                  <table className="bookings-table">
                    <thead>
                      <tr>
                        <th>Nazwa salonu</th>
                        <th>Miasto</th>
                        <th>Adres</th>
                        <th>Kategoria</th>
                        <th>Data złożenia</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map(a => {
                        const s = statusStyle[a.status] || statusStyle.pending;
                        return (
                          <tr key={a.id}>
                            <td><strong>{a.salon_name}</strong></td>
                            <td>{a.city}</td>
                            <td>{a.address}</td>
                            <td>{(categoryIcons[a.category] ? categoryIcons[a.category] + ' ' : '')}{categoryLabels[a.category] || 'Damsko-męski'}</td>
                            <td>{new Date(a.submitted_at).toLocaleDateString('pl-PL')}</td>
                            <td>
                              <span style={{padding:'5px 12px', background:s.bg, color:s.color, borderRadius:'12px', fontSize:'0.85em', fontWeight:700}}>
                                {s.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><Icon name="doc" style={{marginRight:'7px'}} /></div>
                  <h3>Brak wniosków</h3>
                  <button className="cta-button-large" onClick={() => setActiveTab('new')}>Złóż pierwszy wniosek</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* NOWY WNIOSEK */}
        {activeTab === 'new' && (
          <div className="dashboard-content-single">
            <div className="dashboard-card-full">
              <h2><Icon name="add" style={{marginRight:'7px'}} /> Wniosek o nową franczyzę</h2>
              <form onSubmit={handleSubmitApp} className="application-form">
                <div className="form-section">
                  <h3>Dane salonu</h3>
                  <div className="form-grid">
                    <div className="form-group-admin">
                      <label>Nazwa salonu *</label>
                      <input name="salonName" value={appForm.salonName} onChange={handleAppChange}
                        placeholder="np. HAIRKUS Rzeszów Centrum" required />
                    </div>
                    <div className="form-group-admin">
                      <label>Miasto *</label>
                      <input name="city" value={appForm.city} onChange={handleAppChange}
                        placeholder="np. Rzeszów" required />
                    </div>
                    <div className="form-group-admin">
                      <label>Adres *</label>
                      <input name="address" value={appForm.address} onChange={handleAppChange}
                        placeholder="ul. Przykładowa 1" required />
                    </div>
                    <div className="form-group-admin">
                      <label>Powierzchnia (m²)</label>
                      <input name="area" type="number" value={appForm.area} onChange={handleAppChange}
                        placeholder="np. 80" />
                    </div>
                    <div className="form-group-admin" style={{gridColumn:'1/-1'}}>
                      <label>Kategoria salonu *</label>
                      <div style={{display:'flex', gap:'12px', marginTop:'8px'}}>
                        {[
                          { value: 'male',   label: 'Męski',         icon: '♂', color: '#1e3c72' },
                          { value: 'female', label: 'Damski',        icon: '♀', color: '#ec4899' },
                          { value: 'both',   label: 'Damsko-męski',  icon: '♂♀', color: '#059669' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAppForm(f => ({ ...f, category: opt.value }))}
                            style={{
                              flex: 1,
                              padding: '14px',
                              border: `2px solid ${appForm.category === opt.value ? opt.color : '#e0e0e0'}`,
                              borderRadius: '12px',
                              background: appForm.category === opt.value ? opt.color + '15' : 'white',
                              color: appForm.category === opt.value ? opt.color : '#666',
                              fontWeight: 600,
                              fontSize: '0.95em',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <span style={{fontSize:'1.5em'}}>{opt.icon}</span>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-section">
                  <h3>Opis i doświadczenie</h3>
                  <textarea name="description" value={appForm.description} onChange={handleAppChange}
                    rows="5"
                    placeholder="Opisz swoje doświadczenie w branży, plany dotyczące salonu, dlaczego chcesz otworzyć salon HAIRKUS..."
                    style={{width:'100%', padding:'15px', border:'2px solid #e0e0e0', borderRadius:'8px', fontSize:'1em', fontFamily:'inherit', resize:'vertical'}}
                  />
                </div>
                <div style={{display:'flex', gap:'12px', justifyContent:'flex-end'}}>
                  <button type="button" className="profile-cancel-btn" onClick={() => setActiveTab('applications')}>Anuluj</button>
                  <button type="submit" className="cta-button-large" disabled={submitting}>
                    {submitting ? 'Wysyłanie...' : 'Wyślij wniosek'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ZAMÓWIENIA B2B */}
        {activeTab === 'b2b' && (
          <div className="dashboard-content-single">
            <B2BPanel user={user} approvedSalons={approvedSalons} />
          </div>
        )}

        {/* SZKOLENIA */}
        {activeTab === 'trainings' && (
          <div className="dashboard-content-single">
            <TrainingsPanel user={user} />
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="dashboard-content-single">
            <NotificationsPanel user={user} />
          </div>
        )}

        {/* WIADOMOŚCI */}
        {activeTab === 'messages' && (
          <div className="dashboard-content-single">
            <div className="dashboard-card-full">
              <h2><Icon name="chat" style={{marginRight:'7px'}} /> Kontakt z Administratorem</h2>
              <p style={{color:'#666', marginBottom:'20px'}}>
                Masz pytania dotyczące franczyzy? Napisz bezpośrednio do administratora sieci HAIRKUS.
              </p>
              <Chat
                conversationId={`franchisee_${user?.id}_admin`}
                recipientName="Administrator HAIRKUS"
                recipientRole="admin"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FranchiseeDashboard;